/**
 * SurveyJS Service
 *
 * Utility service to interact with SurveyJS Model API
 * Handles all survey and question management operations using SurveyJS library
 */

import { QuestionType } from '@/contracts/enums/questionTypes';

import {
  EditorQuestion,
  QuestionValidationError,
  ValidationErrors,
} from '@/contracts/models/survey-editor';
import { OptionItem } from '@/contracts/models/survey';
import { createOption } from '@/utils/optionId';
import {
  ISurvey,
  Model,
  Question,
  QuestionBooleanModel,
  QuestionCheckboxModel,
  QuestionCommentModel,
  QuestionDropdownModel,
  QuestionMatrixModel,
  QuestionRadiogroupModel,
  QuestionRatingModel,
  QuestionTextModel,
} from 'survey-core';

export const SUPPORTED_QUESTION_TYPES = [
  'text',
  'comment',
  'boolean',
  'radiogroup',
  'rating',
  'checkbox',
  'dropdown',
  'tagbox',
  'matrix',
] as const;

export type SupportedQuestionType = (typeof SUPPORTED_QUESTION_TYPES)[number];

/**
 * Default option sets for newly created questions. Factory functions (not
 * shared constants) so every new question gets options with fresh stable ids
 * rather than aliasing one mutable array.
 */
export const makeDefaultChoices = (): OptionItem[] => [
  createOption(),
  createOption(),
];
export const makeDefaultMatrixRows = (): OptionItem[] => [
  createOption(),
  createOption(),
];
export const makeDefaultMatrixColumns = (): OptionItem[] => [
  createOption(),
  createOption(),
  createOption(),
];

/**
 * Question configuration for creating new questions
 */
export interface QuestionConfig {
  type: SupportedQuestionType;
  name: string;
  title: string;
  isRequired?: boolean;

  // Type-specific properties
  choices?: string[] | { value: string; text: string }[];
  maxLength?: number;
  minRateDescription?: string;
  maxRateDescription?: string;
  rateMin?: number;
  rateMax?: number;

  // Matrix-specific
  rows?: string[] | { value: string; text: string }[];
  columns?: string[] | { value: string; text: string }[];
}

/**
 * SurveyJS Service Class
 */
export class SurveyJsService {
  private model: Model;

  constructor(surveyJson?: ISurvey) {
    this.model = new Model(
      surveyJson || { pages: [{ name: 'page1', elements: [] }] }
    );
  }

  /**
   * Get the current survey JSON
   */
  getSurveyJson(): ISurvey {
    return this.model.toJSON();
  }

  /**
   * Get the SurveyJS Model instance
   */
  getModel(): Model {
    return this.model;
  }

  /**
   * Load a new survey JSON
   */
  loadSurvey(surveyJson: ISurvey): void {
    this.model = new Model(surveyJson);
  }

  /**
   * Set survey title
   */
  setTitle(title: string): void {
    this.model.title = title;
  }

  /**
   * Set survey description
   */
  setDescription(description: string): void {
    this.model.description = description;
  }

  /**
   * Get all questions in page order (preserves the order they appear in the survey)
   */
  getAllQuestions(): Question[] {
    const page = this.model.pages[0];
    if (!page || !page.elements) {
      return [];
    }

    // Filter for questions and maintain the order from page.elements
    const questions: Question[] = [];
    for (const element of page.elements) {
      if (element.isQuestion) {
        questions.push(element as Question);
      }
    }

    return questions;
  }

  /**
   * Get all question names (for debugging)
   */
  getAllQuestionNames(): string[] {
    return this.getAllQuestions().map(q => q.name);
  }

  /**
   * Reorder questions in the SurveyJS model to match the provided order
   */
  reorderQuestions(questionNames: string[]): void {
    const page = this.model.pages[0];
    if (!page || !page.elements) {
      return;
    }

    // Remove all questions from the page
    const questionsToReorder = questionNames
      .map(name => this.getQuestionByName(name))
      .filter(Boolean) as Question[];

    // Remove questions from their current positions
    questionsToReorder.forEach(question => {
      try {
        page.removeElement(question);
      } catch {
        // Question might not be in the page, continue
      }
    });

    // Add questions back in the new order
    questionsToReorder.forEach(question => {
      page.addElement(question);
    });
  }

  /**
   * Clean up orphaned questions (for debugging)
   */
  cleanupOrphanedQuestions(): number {
    const page = this.model.pages[0];
    const allQuestions = this.model.getAllQuestions();
    const pageQuestions = page.elements;

    // Find questions that are in getAllQuestions() but not in page.elements
    const orphanedQuestions = allQuestions.filter(
      q => !pageQuestions.includes(q)
    );

    if (orphanedQuestions.length > 0) {
      // Remove orphaned questions
      orphanedQuestions.forEach(q => {
        try {
          this.model.removeQuestion(q);
        } catch {
          // Silent fail
        }
      });
    }

    return orphanedQuestions.length;
  }

  /**
   * Get a question by name
   */
  getQuestionByName(name: string): Question | undefined {
    return this.model.getQuestionByName(name);
  }

  /**
   * Add a new question to the survey
   * Returns the created question
   */
  addQuestion(config: QuestionConfig): Question {
    const page = this.model.pages[0]; // We use single page for now

    // Create question based on type
    const question = page.addNewQuestion(config.type, config.name);

    // Set common properties
    // Set title to a proper default if not provided, to prevent SurveyJS from using name as title
    question.title = config.title || `Question ${page.elements.length}`;
    if (config.isRequired !== undefined) {
      question.isRequired = config.isRequired;
    }

    // Set type-specific properties
    this.configureQuestionByType(question, config);

    return question;
  }

  /**
   * Update an existing question
   */
  updateQuestion(
    name: string,
    updates: Partial<QuestionConfig>
  ): Question | null {
    const question = this.getQuestionByName(name);
    if (!question) {
      return null;
    }

    // If question type is changing, we need to replace the question
    if (updates.type && updates.type !== question.getType()) {
      const replacedQuestion = this.replaceQuestion(name, updates);
      if (!replacedQuestion) {
        return null;
      }
      return replacedQuestion;
    }

    // Update common properties
    if (updates.title !== undefined) {
      question.title = updates.title;
    }
    if (updates.isRequired !== undefined) {
      question.isRequired = updates.isRequired;
    }

    // Update type-specific properties
    this.configureQuestionByType(question, updates);

    return question;
  }

  /**
   * Replace a question with a new one (used when changing question type)
   */
  private replaceQuestion(
    oldName: string,
    newConfig: Partial<QuestionConfig>
  ): Question | null {
    const oldQuestion = this.getQuestionByName(oldName);
    if (!oldQuestion) {
      return null;
    }

    const page = this.model.getPageByQuestion(oldQuestion);
    if (!page) {
      return null;
    }

    // Validate the new question type
    const newType = newConfig.type || oldQuestion.getType();
    if (!SUPPORTED_QUESTION_TYPES.includes(newType as SupportedQuestionType)) {
      return null;
    }

    // Get the position of the old question
    const oldIndex = page.elements.indexOf(oldQuestion);
    if (oldIndex === -1) {
      return null;
    }

    // Store old question properties before removal
    const oldTitle = oldQuestion.title;
    const oldIsRequired = oldQuestion.isRequired;

    // Remove the old question first to avoid naming conflicts
    page.removeQuestion(oldQuestion);

    // Verify the old question is actually removed
    const stillExists = this.getQuestionByName(oldName);
    if (stillExists) {
      this.model.removeQuestion(stillExists);
    }

    // Create new question with the same name but new type
    const newQuestion = page.addNewQuestion(newType, oldName);

    if (!newQuestion) {
      return null;
    }

    // Copy common properties
    newQuestion.title = newConfig.title ?? oldTitle;
    newQuestion.isRequired = newConfig.isRequired ?? oldIsRequired;

    // Configure type-specific properties
    this.configureQuestionByType(newQuestion, newConfig);

    // Move the new question to the correct position
    const currentIndex = page.elements.indexOf(newQuestion);
    if (currentIndex !== -1 && oldIndex !== -1 && currentIndex !== oldIndex) {
      page.elements.splice(currentIndex, 1);
      page.elements.splice(oldIndex, 0, newQuestion);
    }

    return newQuestion;
  }

  /**
   * Delete a question by name
   */
  deleteQuestion(name: string): boolean {
    const question = this.getQuestionByName(name);
    if (!question) {
      return false;
    }

    const page = this.model.getPageByQuestion(question);
    if (page) {
      page.removeQuestion(question);
      return true;
    }

    return false;
  }

  /**
   * Reorder questions
   */
  reorderQuestion(questionName: string, newIndex: number): boolean {
    const question = this.getQuestionByName(questionName);
    if (!question) {
      return false;
    }

    const page = this.model.pages[0];
    const currentIndex = page.elements.indexOf(question);

    if (currentIndex === -1) {
      return false;
    }

    // Remove from current position
    page.elements.splice(currentIndex, 1);

    // Insert at new position
    page.elements.splice(newIndex, 0, question);

    return true;
  }

  /**
   * Duplicate a question
   */
  duplicateQuestion(name: string): Question | null {
    const question = this.getQuestionByName(name);
    if (!question) {
      return null;
    }

    const page = this.model.getPageByQuestion(question);
    if (!page) {
      return null;
    }

    // Create a copy of the question JSON
    const questionJson = question.toJSON();

    // Generate new unique name
    const newName = `${questionJson.name}_copy_${Date.now()}`;

    // Create the new question using the same method as addQuestion
    const newQuestion = page.addNewQuestion(questionJson.type, newName);
    if (!newQuestion) {
      return null;
    }

    // Set common properties like in addQuestion
    newQuestion.title = questionJson.title ?? '';
    if (questionJson.isRequired !== undefined) {
      newQuestion.isRequired = questionJson.isRequired;
    }

    // Copy type-specific properties
    this.configureQuestionByType(newQuestion, questionJson as QuestionConfig);

    // Manually ensure the question is added to the page's questions array
    if (!page.questions.includes(newQuestion)) {
      page.questions.push(newQuestion);
    }

    return newQuestion;
  }

  /**
   * Configure question based on its type
   */
  private configureQuestionByType(
    question: Question,
    config: Partial<QuestionConfig>
  ): void {
    switch (question.getType()) {
      case 'text':
        this.configureTextQuestion(question as QuestionTextModel, config);
        break;
      case 'comment':
        this.configureCommentQuestion(question as QuestionCommentModel, config);
        break;
      case 'boolean':
        this.configureBooleanQuestion(question as QuestionBooleanModel, config);
        break;
      case 'radiogroup':
        this.configureRadioGroupQuestion(
          question as QuestionRadiogroupModel,
          config
        );
        break;
      case 'rating':
        this.configureRatingQuestion(question as QuestionRatingModel, config);
        break;
      case 'checkbox':
        this.configureCheckboxQuestion(
          question as QuestionCheckboxModel,
          config
        );
        break;
      case 'dropdown':
        this.configureDropdownQuestion(
          question as QuestionDropdownModel,
          config
        );
        break;
      case 'matrix':
        this.configureMatrixQuestion(question as QuestionMatrixModel, config);
        break;
      case 'tagbox':
        this.configureTagboxQuestion(question as QuestionCheckboxModel, config);
        break;
    }
  }

  private configureTextQuestion(
    question: QuestionTextModel,
    config: Partial<QuestionConfig>
  ): void {
    if (config.maxLength !== undefined) {
      question.maxLength = config.maxLength;
    }
  }

  private configureCommentQuestion(
    question: QuestionCommentModel,
    config: Partial<QuestionConfig>
  ): void {
    if (config.maxLength !== undefined) {
      question.maxLength = config.maxLength;
    }
  }

  private configureBooleanQuestion(
    _question: QuestionBooleanModel,
    _config: Partial<QuestionConfig>
  ): void {
    // Boolean questions don't have additional configuration for now
  }

  private configureRadioGroupQuestion(
    question: QuestionRadiogroupModel,
    config: Partial<QuestionConfig>
  ): void {
    if (config.choices !== undefined) {
      question.choices = config.choices;
    }
  }

  private configureRatingQuestion(
    question: QuestionRatingModel,
    config: Partial<QuestionConfig>
  ): void {
    if (config.rateMin !== undefined) {
      question.rateMin = config.rateMin;
    }
    if (config.rateMax !== undefined) {
      question.rateMax = config.rateMax;
    }
    if (config.minRateDescription !== undefined) {
      question.minRateDescription = config.minRateDescription;
    }
    if (config.maxRateDescription !== undefined) {
      question.maxRateDescription = config.maxRateDescription;
    }
  }

  private configureCheckboxQuestion(
    question: QuestionCheckboxModel,
    config: Partial<QuestionConfig>
  ): void {
    if (config.choices !== undefined) {
      question.choices = config.choices;
    }
  }

  private configureDropdownQuestion(
    question: QuestionDropdownModel,
    config: Partial<QuestionConfig>
  ): void {
    if (config.choices !== undefined) {
      question.choices = config.choices;
    }
  }

  private configureMatrixQuestion(
    question: QuestionMatrixModel,
    config: Partial<QuestionConfig>
  ): void {
    if (config.rows !== undefined) {
      question.rows = config.rows;
    }
    if (config.columns !== undefined) {
      question.columns = config.columns;
    }
  }

  private configureTagboxQuestion(
    question: QuestionCheckboxModel,
    config: Partial<QuestionConfig>
  ): void {
    if (config.choices !== undefined) {
      question.choices = config.choices;
    }
  }

  /**
   * Validate the survey
   * Returns true if valid, false otherwise
   */
  validate(): boolean {
    // SurveyJS has built-in validation
    return this.model.validate();
  }

  /**
   * Clear all questions
   */
  clearAllQuestions(): void {
    const page = this.model.pages[0];
    page.elements.splice(0, page.elements.length);
  }

  /**
   * Validate a single question
   */
  validateQuestion(question: EditorQuestion): QuestionValidationError | null {
    const errors: QuestionValidationError = {};

    const hasBlankOption = (options?: { text: string }[]): boolean =>
      (options ?? []).some(option => !option.text || option.text.trim() === '');

    // Title is required
    if (!question.title || question.title.trim() === '') {
      errors.title = 'editor.validation.questionTitleRequired';
    }

    // Type-specific validation
    switch (question.type) {
      case QuestionType.RadioGroup:
      case QuestionType.Checkbox:
      case QuestionType.Dropdown:
      case QuestionType.MultiSelectDropdown:
        if (!question.choices || question.choices.length < 2) {
          errors.choices = 'editor.validation.choicesMinimum';
        } else if (hasBlankOption(question.choices)) {
          errors.choices = 'editor.validation.choiceTextRequired';
        }
        break;

      case QuestionType.Rating:
        if (question.rateMin == null || question.rateMin < 1) {
          errors.rateMin = 'editor.validation.ratingMinRequired';
        } else if (question.rateMin > 19) {
          errors.rateMin = 'editor.validation.ratingMinMax';
        }
        if (question.rateMax == null || question.rateMax < 1) {
          errors.rateMax = 'editor.validation.ratingMaxRequired';
        } else if (question.rateMax > 20) {
          errors.rateMax = 'editor.validation.ratingMaxMax';
        }
        if (
          question.rateMin != null &&
          question.rateMin > 0 &&
          question.rateMin < 20 &&
          question.rateMax != null &&
          question.rateMax > 0 &&
          question.rateMax < 21 &&
          question.rateMin >= question.rateMax
        ) {
          errors.rateMin = 'editor.validation.ratingInvalid';
          errors.rateMax = 'editor.validation.ratingInvalid';
        }
        break;

      case QuestionType.Text:
      case QuestionType.Comment:
        if (
          question.maxLength !== undefined &&
          question.maxLength !== null &&
          question.maxLength < 1
        ) {
          errors.maxLength = 'editor.validation.maxLengthMinimum';
        }
        break;

      case QuestionType.Matrix:
        if (!question.rows || question.rows.length < 1) {
          errors.rows = 'editor.validation.matrixRowsRequired';
        } else if (hasBlankOption(question.rows)) {
          errors.rows = 'editor.validation.matrixRowTextRequired';
        }
        if (!question.columns || question.columns.length < 2) {
          errors.columns = 'editor.validation.matrixColumnsRequired';
        } else if (hasBlankOption(question.columns)) {
          errors.columns = 'editor.validation.matrixColumnTextRequired';
        }
        break;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }

  /**
   * Validate the entire survey
   */
  validateSurvey(survey: {
    title: string;
    description?: string;
    completionMessage?: string;
    questions: EditorQuestion[];
  }): ValidationErrors {
    const errors: ValidationErrors = {};

    if (!survey.title || survey.title.trim() === '') {
      errors.title = 'editor.validation.titleRequired';
    }

    if (!survey.description || survey.description.trim() === '') {
      errors.description = 'editor.validation.descriptionRequired';
    }

    if (!survey.completionMessage || survey.completionMessage.trim() === '') {
      errors.completionMessage = 'editor.validation.completionMessageRequired';
    }

    if (survey.questions.length === 0) {
      errors.title = errors.title
        ? `${errors.title}. editor.validation.questionsRequired`
        : 'editor.validation.questionsRequired';
    }

    const questionErrors: Record<string, QuestionValidationError> = {};
    // Branch children are real questions nested under their parent — validate
    // them too (e.g. a branch child left without a title).
    const allQuestions = survey.questions.flatMap(question => [
      question,
      ...(question.branchChildren ?? []),
    ]);
    allQuestions.forEach(question => {
      const questionError = this.validateQuestion(question);
      if (questionError) {
        questionErrors[question.id] = questionError;
      }
    });

    if (Object.keys(questionErrors).length > 0) {
      errors.questions = questionErrors;
    }

    return errors;
  }
}

/**
 * Factory function to create a new SurveyJsService instance
 */
export function createSurveyJsService(surveyJson?: ISurvey): SurveyJsService {
  return new SurveyJsService(surveyJson);
}
